import { useMemo } from 'react'

import { useQuery } from 'react-query'

import { LegalEntity } from '@webapp/platform/legal-entities/entities/LegalEntity'
import { legalEntityQueryConfig } from '@webapp/platform/legal-entities/query-configs/legalEntityQueryConfig'

interface UseQueryLegalEntitiesOptions {
  enabled?: boolean
  onError?: () => void
}

interface UseQueryLegalEntitiesReturn {
  entities: LegalEntity[]
  isFetching: boolean
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

/**
 * Fetches every LegalEntity (active and inactive) in a single request off the
 * shared `list` query config, so every consumer across modules dedupes onto
 * one cache entry. Filtering, searching and paging happen client-side in the
 * consumer; `useQueryActiveLegalEntities` is the active-only selection.
 */
export const useQueryLegalEntities = (
  options: UseQueryLegalEntitiesOptions = {}
): UseQueryLegalEntitiesReturn => {
  const { data, isFetching, isLoading, isError, refetch } = useQuery({
    ...legalEntityQueryConfig.list(),
    enabled: options.enabled ?? true,
    onError: options.onError,
  })

  const entities = useMemo(
    () => (data ?? []).map((entity) => new LegalEntity(entity)),
    [data]
  )

  return {
    entities,
    isFetching,
    isLoading,
    isError,
    refetch,
  }
}
